/**
 * UI-neutral metadata for every product export control.
 *
 * Domain models and exporters still own their data and execution. This registry
 * only keeps user-facing names, formats, and report ownership consistent across
 * the editor surfaces.
 */
export type ExportActionDomain =
    | 'breakdown'
    | 'stripboard'
    | 'dood'
    | 'vfx'
    | 'call-sheet'
    | 'synopsis'
    | 'sides';

export type ExportActionFormat = 'xlsx' | 'csv' | 'pdf' | 'fdx' | 'txt' | 'print' | 'bundle';

export interface ExportActionDefinition {
    readonly id: ExportActionId;
    readonly domain: ExportActionDomain;
    readonly format: ExportActionFormat;
    readonly label: string;
    readonly shortLabel: string;
    readonly extension: string | null;
}

const defineExportActions = <
    T extends Record<string, Omit<ExportActionDefinition, 'id'>>,
>(actions: T): { readonly [K in keyof T]: Readonly<T[K] & { id: K }> } => (
    Object.freeze(Object.fromEntries(
        Object.entries(actions).map(([id, action]) => [id, Object.freeze({ id, ...action })]),
    )) as { readonly [K in keyof T]: Readonly<T[K] & { id: K }> }
);

const ACTION_DEFINITIONS = {
    'sides-print': {
        domain: 'sides', format: 'print', label: 'Print / Save PDF', shortLabel: 'Print / PDF', extension: null,
    },
    'breakdown-pdf': {
        domain: 'breakdown', format: 'pdf', label: 'Breakdown PDF', shortLabel: 'PDF', extension: '.pdf',
    },
    'breakdown-xlsx': {
        domain: 'breakdown', format: 'xlsx', label: 'Breakdown XLSX', shortLabel: 'XLSX', extension: '.xlsx',
    },
    'breakdown-csv': {
        domain: 'breakdown', format: 'csv', label: 'Breakdown CSV', shortLabel: 'CSV', extension: '.csv',
    },
    'breakdown-all': {
        domain: 'breakdown', format: 'bundle', label: 'All breakdown formats', shortLabel: 'All', extension: null,
    },
    'script-fdx': {
        domain: 'breakdown', format: 'fdx', label: 'Final Draft FDX', shortLabel: 'FDX', extension: '.fdx',
    },
    'synopsis-txt': {
        domain: 'synopsis', format: 'txt', label: 'Synopsis TXT', shortLabel: 'TXT', extension: '.txt',
    },
    'stripboard-xlsx': {
        domain: 'stripboard', format: 'xlsx', label: 'Stripboard XLSX', shortLabel: 'XLSX', extension: '.xlsx',
    },
    'stripboard-csv': {
        domain: 'stripboard', format: 'csv', label: 'Stripboard CSV', shortLabel: 'CSV', extension: '.csv',
    },
    'stripboard-pdf': {
        domain: 'stripboard', format: 'pdf', label: 'Stripboard PDF', shortLabel: 'PDF', extension: '.pdf',
    },
    'dood-xlsx': {
        domain: 'dood', format: 'xlsx', label: 'DOOD XLSX', shortLabel: 'XLSX', extension: '.xlsx',
    },
    'dood-print': {
        domain: 'dood', format: 'print', label: 'Print DOOD report', shortLabel: 'Print', extension: null,
    },
    'vfx-xlsx': {
        domain: 'vfx', format: 'xlsx', label: 'VFX XLSX', shortLabel: 'XLSX', extension: '.xlsx',
    },
    'call-sheet-pdf': {
        domain: 'call-sheet', format: 'pdf', label: 'Call Sheet PDF', shortLabel: 'PDF', extension: '.pdf',
    },
    'call-sheet-print': {
        domain: 'call-sheet', format: 'print', label: 'Print Call Sheet', shortLabel: 'Print', extension: null,
    },
    'report-print': {
        domain: 'breakdown', format: 'print', label: 'Print report', shortLabel: 'Print', extension: null,
    },
} as const;

export type ExportActionId = keyof typeof ACTION_DEFINITIONS;
export type StripboardExportActionId = Extract<ExportActionId, `stripboard-${string}`>;

export const EXPORT_ACTIONS = defineExportActions(ACTION_DEFINITIONS);

export const EXPORT_ACTION_IDS = Object.freeze(
    Object.keys(EXPORT_ACTIONS) as ExportActionId[],
);

export const getExportAction = (id: ExportActionId): ExportActionDefinition => (
    EXPORT_ACTIONS[id]
);

export const getExportActionsForDomain = (
    domain: ExportActionDomain,
): readonly ExportActionDefinition[] => (
    EXPORT_ACTION_IDS
        .map(id => EXPORT_ACTIONS[id])
        .filter(action => action.domain === domain)
);
